/**
 * Presentation helpers. Keeping these out of components makes the JSX easier to
 * scan and gives us one place to swap in `Intl` formatting later.
 */

/** @param {number} count @returns {string} e.g. `"1 guest"`, `"4 guests"` */
export const guestLabel = (count) => `${count} ${count === 1 ? 'guest' : 'guests'}`;

/**
 * Deterministic placeholder artwork for a restaurant.
 *
 * Renders an inline SVG rather than pulling from a stock-photo service: random
 * stock images look broken in a restaurant app, and this needs no network, never
 * 404s, and is unmistakably a placeholder.
 *
 * Real photography arrives automatically once `GOOGLE_PLACES_KEY` is set — see
 * `RestaurantImage`, which prefers a live photo whenever one is available.
 *
 * @param {string} seed  Stable per-restaurant seed; picks the hue.
 * @returns {string} A `data:image/svg+xml` URL.
 */
export function placeholderPhoto(seed) {
  const hue = hashToRange(seed, 6, 46);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 800">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="0.7" y2="1">
      <stop offset="0%" stop-color="hsl(${hue},68%,74%)"/>
      <stop offset="100%" stop-color="hsl(${hue - 14},52%,56%)"/>
    </linearGradient>
  </defs>
  <rect width="600" height="800" fill="url(#g)"/>
  <g opacity="0.3" stroke="#fff" stroke-width="1.6" stroke-linecap="round" fill="none"
     transform="translate(204,254) scale(8)">
    <path d="M3 2v7c0 1.1.9 2 2 2h4v13"/><path d="M17 2v20"/><path d="M12 2v7c0 1.1-.9 2-2 2"/>
  </g>
</svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Maps a string onto an integer range, so a seed always yields the same hue.
 *
 * @param {string} value
 * @param {number} min
 * @param {number} max
 */
function hashToRange(value, min, max) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 100000;
  }
  return min + (hash % (max - min + 1));
}
